import { right, wrong, type Either } from '@/core/either'
import { config } from '@/default'
import { showDefaultToast } from '@/helpers/toast-models'
import { pinia } from '@/stores'
import { useUserStore } from '@/stores/user.store'
import * as Sentry from '@sentry/vue'
import type { AxiosError, AxiosInstance, AxiosRequestHeaders, AxiosResponse } from 'axios'
import axios from 'axios'
import { errorResponseMap } from './constraints'
import { ApiError } from './errors'

type ApiResponse<SuccessResponse> = Promise<Either<ApiError, SuccessResponse>>

export function initService(): AxiosInstance {
  const httpClient = axios.create({
    baseURL: config.warranty.baseUrl as string
  })

  httpClient.defaults.baseURL = config.warranty.baseUrl as string
  httpClient.defaults.headers.common['Content-Type'] = 'application/json'

  return httpClient
}

export async function setRequestInterceptors(client: AxiosInstance) {
  const userStore = useUserStore()

  client.interceptors.request.use(
    (requestConfig) => {
      if (requestConfig.url && typeof requestConfig.url === 'string') {
        requestConfig.url = `${config.warranty.baseUrl}${requestConfig.url.replace(/\/+$/, '')}` // Remove barras no final da URL
      }

      requestConfig.headers = {
        ...requestConfig.headers,
        Authorization: `Bearer ${userStore.jwtToken ?? ''}`,
        'Accept-Language': 'pt-BR',
        'ngrok-skip-browser-warning': 'xpto'
      } as unknown as AxiosRequestHeaders

      return requestConfig
    },
    (error: any) => {
      return Promise.reject(error)
    }
  )

  return client
}

const EXPECTED_ERRORS_WITHOUT_TOAST = ['TagFromDuplicatedFlyerError']

export async function setResponseInterceptors(client: AxiosInstance) {
  const userStore = useUserStore(pinia)

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const errorStatus = error.response?.status
      let errorName = 'Error'

      let errorMsg = errorResponseMap({
        errorStatus,
        routeName: error.config?.method?.toUpperCase()
      })

      const errorsToLogout = [
        'ExpiredOrRevokedOperatorAccessToken',
        'TokenExpiredError',
        'InvalidOperatorAccessToken'
      ]

      const isTokenError =
        errorStatus === 401 ||
        (error.response?.data && errorsToLogout.includes(error.response.data.name))

      if (error.response?.data) {
        errorName = error.response.data.name
        errorMsg = error.response.data.message
      }

      // Erros esperados que a tela trata sozinha (ex.: modal de cartela duplicada): sem toast e sem Sentry
      if (EXPECTED_ERRORS_WITHOUT_TOAST.includes(errorName)) {
        return Promise.reject(new ApiError(errorName, errorMsg, error.response?.data?.params))
      }

      if (isTokenError) {
        if (!userStore.jwtToken) {
          return Promise.reject(new ApiError(errorName, errorMsg))
        }
        userStore.actions.logout()
      }

      showDefaultToast(errorMsg, 'error')

      Sentry.captureException(error)

      return Promise.reject(new ApiError(errorName, errorMsg))
    }
  )
}

export async function handleAxiosResponse<SuccessResponse>(
  request: Promise<AxiosResponse>
): ApiResponse<SuccessResponse> {
  try {
    const response = await request

    return right(response.data as SuccessResponse)
  } catch (error) {
    const axiosError = error as AxiosError<ApiError>
    return wrong(axiosError)
  }
}
